import React, { createContext, useReducer, useMemo, useEffect } from 'react'
import filter from 'lodash/filter'
import uniqBy from 'lodash/uniqBy'
import uniqueId from 'lodash/uniqueId'
import cloneDeep from 'lodash/cloneDeep'

// import { useShallowEqualSelector } from 'custom-hooks'
// import { getAlertsDataSelector } from 'redux/reducers/alerts'
import AlertsPage from './AlertsPage'
import { useAlertApi } from './useAlertApi'

export const AlertContext = createContext({})

export const statusValueConstants = {
  RESOLVE: 'Resolved',
  OPEN: 'Open',
}

const filterFieldsValueConstants = {
  FACILITY: 'facility',
  ISSUE_DEVICE_TYPE: 'issueDeviceType',
  SEARCH_FIELD: 'issue',
  STATUS: 'status',
}

const calculateStatusCounts = (
  data = [],
  filterFields = [],
  searchValue = ''
) => {
  let _copyData = cloneDeep(data)

  const filter = (data = '', field = '', value = null) => {
    return data.filter((dataItem) => dataItem[field] === value)
  }

  filterFields.forEach((filItem) => {
    if (filItem.value) {
      _copyData = filter(_copyData, filItem.field, filItem.value)
    }
  })

  if (searchValue) {
    _copyData = _copyData.filter((item) =>
      String(item[filterFieldsValueConstants.SEARCH_FIELD])
        .toLowerCase()
        .includes(String(searchValue).toLowerCase())
    )
  }

  if (!_copyData.length) return { all: 0, open: 0, resolve: 0 }

  return _copyData.reduce(
    (res, item) => {
      res.all += 1
      item[filterFieldsValueConstants.STATUS] === statusValueConstants.RESOLVE
        ? res.resolve++
        : res.open++

      return res
    },
    { all: 0, open: 0, resolve: 0 }
  )
}

const getFilterOptions = (data, field) => {
  return uniqBy(data, field).map((f) => ({
    id: uniqueId(`${field}_`),
    label: f[field],
    value: f[field],
  }))
}

const actionsTypes = {
  SET_STATUS_TAB_VALUE: 'SET_STATUS_TAB_VALUE',
  SET_FACILITY_SELECT_VALUE: 'SET_FACILITY_SELECT_VALUE',
  SET_DEVICES_SELECT_VALUE: 'SET_DEVICES_SELECT_VALUE',
  SET_SEARCH_VALUE: 'SET_SEARCH_VALUE',
  SET_FILTERING_DATA: 'SET_FILTERING_DATA',
  SET_STATUS_COUNTS: 'SET_STATUS_COUNTS',
  SET_FACILITY_FILTER_OPTIONS: 'SET_FACILITY_FILTER_OPTIONS',
  SET_DEVICES_FILTER_OPTIONS: 'SET_DEVICES_FILTER_OPTIONS',
}

export const facilityFiltedDefaultValue = {
  id: 'all',
  label: 'All facilities',
  value: null,
}

export const deviceFilterDefaultValue = {
  id: 'all',
  label: 'All devices',
  value: null,
}

const initialState = {
  statusTabValue: null,
  facilitySelectValue: facilityFiltedDefaultValue,
  devicesSelectValue: deviceFilterDefaultValue,
  searchValue: '',
  filteringData: [],
  statusCounts: {},
  facilityFilterOptions: [],
  devicesFilterOptions: [],
}

function reducer(state, action) {
  switch (action.type) {
    case actionsTypes.SET_STATUS_TAB_VALUE:
      return { ...state, statusTabValue: action.payload }
    case actionsTypes.SET_FACILITY_SELECT_VALUE:
      return { ...state, facilitySelectValue: action.payload }
    case actionsTypes.SET_DEVICES_SELECT_VALUE:
      return { ...state, devicesSelectValue: action.payload }
    case actionsTypes.SET_SEARCH_VALUE:
      return { ...state, searchValue: action.payload }
    case actionsTypes.SET_FILTERING_DATA:
      return { ...state, filteringData: action.payload }
    case actionsTypes.SET_STATUS_COUNTS:
      return { ...state, statusCounts: action.payload }
    case actionsTypes.SET_FACILITY_FILTER_OPTIONS:
      return {
        ...state,
        facilityFilterOptions: action.payload,
      }
    case actionsTypes.SET_DEVICES_FILTER_OPTIONS:
      return {
        ...state,
        devicesFilterOptions: action.payload,
      }
    default:
      return state
  }
}

const dispatchActions = (dispatch) => ({
  setStatusTabValue(value) {
    dispatch({ type: actionsTypes.SET_STATUS_TAB_VALUE, payload: value })
  },
  setFacilitySelectValue(value) {
    dispatch({ type: actionsTypes.SET_FACILITY_SELECT_VALUE, payload: value })
  },
  setDevicesSelectValue(value) {
    dispatch({ type: actionsTypes.SET_DEVICES_SELECT_VALUE, payload: value })
  },
  setSearchValue(value) {
    dispatch({ type: actionsTypes.SET_SEARCH_VALUE, payload: value })
  },
  setFilteringData(value) {
    dispatch({ type: actionsTypes.SET_FILTERING_DATA, payload: value })
  },
  setStatusCounts(value) {
    dispatch({ type: actionsTypes.SET_STATUS_COUNTS, payload: value })
  },
  setFacilityFilterOptions(value) {
    dispatch({ type: actionsTypes.SET_FACILITY_FILTER_OPTIONS, payload: value })
  },
  setDevicesFilterOptions(value) {
    dispatch({ type: actionsTypes.SET_DEVICES_FILTER_OPTIONS, payload: value })
  },
})

const AlertContainer = () => {
  const [alertState, dispatch] = useReducer(reducer, initialState)

  const actions = useMemo(() => dispatchActions(dispatch), [dispatch])

  const {
    setStatusCounts,
    setFilteringData,
    setFacilityFilterOptions,
    setDevicesFilterOptions,
  } = actions

  const {
    statusTabValue,
    facilitySelectValue,
    devicesSelectValue,
    searchValue,
  } = alertState

  const { data: alertData } = useAlertApi()

  useEffect(() => {
    setFacilityFilterOptions(
      getFilterOptions(alertData, filterFieldsValueConstants.FACILITY)
    )
    setDevicesFilterOptions(
      getFilterOptions(alertData, filterFieldsValueConstants.ISSUE_DEVICE_TYPE)
    )
  }, [alertData, setFacilityFilterOptions, setDevicesFilterOptions])

  useEffect(() => {
    const params = {}
    if (statusTabValue)
      params[filterFieldsValueConstants.STATUS] = statusTabValue
    if (facilitySelectValue.value)
      params[filterFieldsValueConstants.FACILITY] = facilitySelectValue.value
    if (devicesSelectValue.value)
      params[filterFieldsValueConstants.ISSUE_DEVICE_TYPE] =
        devicesSelectValue.value

    setFilteringData(
      filter(alertData, params).filter((d) =>
        String(d[filterFieldsValueConstants.SEARCH_FIELD])
          .toLowerCase()
          .includes(String(searchValue).toLowerCase())
      )
    )
  }, [
    alertData,
    setFilteringData,
    statusTabValue,
    facilitySelectValue,
    devicesSelectValue,
    searchValue,
  ])

  useEffect(() => {
    setStatusCounts(
      calculateStatusCounts(
        alertData,
        [
          {
            field: filterFieldsValueConstants.FACILITY,
            value: facilitySelectValue.value,
          },
          {
            field: filterFieldsValueConstants.ISSUE_DEVICE_TYPE,
            value: devicesSelectValue.value,
          },
        ],
        searchValue
      )
    )
  }, [
    alertData,
    setStatusCounts,
    facilitySelectValue,
    devicesSelectValue,
    searchValue,
  ])

  return (
    <AlertContext.Provider value={{ state: alertState, actions }}>
      <AlertsPage />
    </AlertContext.Provider>
  )
}

export default AlertContainer

// issue: 'Some issue with HVAC, need to be fixed',
// issueDeviceType: 'HVAC',
// assignee: 'Guy Hawkins',
// created: 'Bob Luchiano',
// facility: 'Al A’shaari St',
// panel: 'Rear of 1st floor',
// status: 'resolve',
// dateCreated: '09 Oct at 14:54',
// id: uniqueId('alerts_'),
